import { Flex, Box, Button, Menu, Portal } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { LuLogOut, LuCircleUser, LuImage } from 'react-icons/lu';
import { FaWhatsapp } from 'react-icons/fa';
import { LanguageMenuItems } from './LanguageSwitcher';
import logo from '../assets/logo/logo.svg';

interface HeaderProps {
  businessId?: string;
  actions?: React.ReactNode;
}

const Header = ({ actions }: HeaderProps) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      toast.success(t('auth.loggedOutSuccess'));
    } catch {
      toast.error(t('auth.logoutFailed'));
    }
  };

  return (
    <Flex
      as="header"
      height="56px"
      px={{ base: '16px', md: '24px' }}
      alignItems="center"
      justifyContent="space-between"
      borderBottomWidth="1px"
      borderColor="neutral.700"
      backgroundColor="neutral.900"
      flexShrink={0}
    >
      {/* Logo */}
      <Box
        cursor="pointer"
        onClick={() => navigate('/')}
      >
        <img src={logo} alt="Menu" style={{ height: 18 }} />
      </Box>

      <Flex alignItems="center" gap="12px">
        {actions}

      {/* User Menu */}
      <Menu.Root>
        <Menu.Trigger asChild>
          <Button
            height="fit-content"
            p="0"
            fontWeight="400"
            fontSize="13px"
            color="neutral.500"
            backgroundColor="transparent"
            borderWidth="0"
            _hover={{ color: 'neutral.300' }}
          >
            <Flex
              alignItems="center"
              justifyContent="center"
              width="32px"
              height="32px"
              backgroundColor="neutral.700"
              borderRadius="50%"
              fontSize="13px"
              fontWeight="500"
              color="neutral.100"
            >
              {user?.email?.charAt(0).toUpperCase() || '?'}
            </Flex>
          </Button>
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content
              backgroundColor="neutral.800"
              borderWidth="1px"
              borderColor="neutral.700"
              borderRadius="8px"
              minWidth="220px"
              p="8px"
            >
              {/* User email */}
              <Box
                px="12px"
                py="8px"
                fontSize="12px"
                fontWeight="400"
                color="neutral.500"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {user?.email}
              </Box>

              <Box height="1px" backgroundColor="neutral.700" my="4px" />

              <Menu.Item
                value="profile"
                onClick={() => navigate('/profile')}
                fontSize="13px"
                fontWeight="400"
                color="neutral.400"
                borderRadius="6px"
                px="12px"
                py="8px"
                _hover={{
                  backgroundColor: 'neutral.700',
                  color: 'neutral.100',
                }}
                cursor="pointer"
              >
                <Flex alignItems="center" gap="10px">
                  <LuCircleUser style={{ width: 15, height: 15 }} />
                  {t('nav.profile')}
                </Flex>
              </Menu.Item>

              {user?.email === 'andiskim@gmail.com' && (
                <Menu.Item
                  value="stock-images"
                  onClick={() => navigate('/admin/stock-images')}
                  fontSize="13px"
                  fontWeight="400"
                  color="neutral.400"
                  borderRadius="6px"
                  px="12px"
                  py="8px"
                  _hover={{
                    backgroundColor: 'neutral.700',
                    color: 'neutral.100',
                  }}
                  cursor="pointer"
                >
                  <Flex alignItems="center" gap="10px">
                    <LuImage style={{ width: 15, height: 15 }} />
                    Stock Images
                  </Flex>
                </Menu.Item>
              )}

              <Menu.Item
                value="support"
                onClick={() => window.open('https://wa.me/7143352460', '_blank')}
                fontSize="13px"
                fontWeight="400"
                color="neutral.400"
                borderRadius="6px"
                px="12px"
                py="8px"
                _hover={{
                  backgroundColor: 'neutral.700',
                  color: 'neutral.100',
                }}
                cursor="pointer"
              >
                <Flex alignItems="center" gap="10px">
                  <FaWhatsapp style={{ width: 15, height: 15 }} />
                  {t('nav.support')}
                </Flex>
              </Menu.Item>

              <Box height="1px" backgroundColor="neutral.700" my="4px" />

              <LanguageMenuItems />

              <Box height="1px" backgroundColor="neutral.700" my="4px" />

              <Menu.Item
                value="logout"
                onClick={handleLogout}
                fontSize="13px"
                fontWeight="400"
                color="error.600"
                borderRadius="6px"
                px="12px"
                py="8px"
                _hover={{
                  backgroundColor: 'neutral.700',
                  color: 'error.600',
                }}
                cursor="pointer"
              >
                <Flex alignItems="center" gap="10px">
                  <LuLogOut style={{ width: 15, height: 15 }} />
                  {t('auth.logout')}
                </Flex>
              </Menu.Item>
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
      </Flex>
    </Flex>
  );
};

export default Header;
